import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Decimal } from 'decimal.js';
import {
  HasQuoteId,
  IBaseQuote,
  IMaintenanceQuote,
  IMaintenanceQuoteFormGroup,
  IQuote,
  IQuoteFormGroup,
  IQuoteItem,
  IQuoteItemFormGroup,
  IQuoteMetaFormGroup,
  IQuotePriceAdjustmentFormGroup,
  IPhysicalAggregateQuoteItem,
  PriceAdjustmentType,
  ISumOfPartsAggregateQuoteItem,
  IMaintenanceContractLineItem,
  IQuoteMeta,
} from './interfaces';
import { FormGroupIdDecorator } from '../../../core/forms';
import { QuoteItemFormGroupWithId } from '../../shared/types';
import { QuoteType } from './enums';
import { uniqueQuoteReferenceValidator } from '../../../controls/shared/validators';
import { QuoteDataGatewayService } from '../../shared/quote-data-gateway.service';
import { Delta } from 'quill/core';

export enum SepratorStyle {
  SOLID = 1,
  DOUBLELINE,
  DASHED,
  DOTTED,
}

export enum ItemType {
  PART = 'part',
  FINISHEDGOOD = 'finished_good',
  COMPOSITE = 'composite',
  ENCLOSURE = 'enclosure',
  NETTPRICE = 'nett_price',
}

export class IQuoteItemWithContextDecorator implements Partial<IQuoteItem> {
  private _item: Partial<IQuoteItem> | undefined;
  private _context: number[] = [];
  private _insertat = 0;

  constructor(item?: Partial<IQuoteItem>, context?: number[], insertAt = 0) {
    this._item = item;
    this._context = context || [];
    this._insertat = insertAt;
  }

  get id(): number {
    return this._item?.id || 0;
  }

  get item_part_id(): string {
    return this._item?.item_part_id || '';
  }

  get item_part_type() {
    return this._item?.item_part_type;
  }

  get context(): number[] {
    return this._context;
  }

  get parentcontext(): number {
    if (this._context) {
      return this._context[this._context.length - 1];
    }

    return 0;
  }

  get insertAt(): number {
    return this._insertat;
  }
}

export abstract class BaseQuote<T extends HasQuoteId> {
  quote_id: string | undefined;
  protected _formgroup!: FormGroup | null;
  protected quoteCurrentLineNumber = 1;
  protected gateway: QuoteDataGatewayService | undefined = undefined;

  //@TODO: define the FormGroup Type properly so you can access the properties via `.`
  /*get quote_items():
    | FormArray<FormGroupIdDecorator<IQuoteItemFormGroup>>
    | undefined {
    return this._formgroup?.controls['quote_items'] as unknown as FormArray<
      FormGroupIdDecorator<IQuoteItemFormGroup>
    >;
  }*/

  abstract get total(): Decimal;
  abstract get total_inc_markup(): Decimal;

  constructor(quote: T | undefined, gateway?: QuoteDataGatewayService) {
    this.quote_id = quote?.quote_id;
    this.gateway = gateway;
  }

  //@TODO: better definition for FormGroup types required.
  //@TODO: define TypedFormGroup interface
  abstract make_formgroup(quote: T): FormGroup;

  setLineNumber(lineNumber = 1) {
    this.quoteCurrentLineNumber = lineNumber;
  }

  public getQuoteItemById(
    id: string,
    container:
      | FormArray<FormGroupIdDecorator<IQuoteItemFormGroup>>
      | undefined = undefined,
  ): FormGroup<IQuoteItemFormGroup> | undefined {
    if (!container) {
      container = this._formgroup?.controls['quote_items'] as FormArray<
        FormGroupIdDecorator<IQuoteItemFormGroup>
      >;
      if (!container) {
        return undefined;
      }
    }

    for (const item of container.controls) {
      if (item.controls['quote_item_id'].value == id) {
        return item as unknown as FormGroup<IQuoteItemFormGroup>;
      }

      if (item.parts) {
        const match = this.getQuoteItemById(id, item.parts);

        if (match) {
          return match;
        }
      }
    }

    return undefined;
  }

  public getQuoteItemAtLine(
    linenumber: number,
    container:
      | FormArray<FormGroup<IQuoteItemFormGroup>>
      | undefined = undefined,
    match: Partial<IQuoteItem> | undefined = undefined,
    context: number[] = [],
  ): IQuoteItemWithContextDecorator | undefined {
    if (!container) {
      container = this._formgroup?.controls['quote_items'] as FormArray<
        FormGroup<IQuoteItemFormGroup>
      >;
      if (!container) {
        return undefined;
      }
    }

    let insertAt = 0;
    for (const item of container!.controls) {
      if (item.controls.item_line_number.value == linenumber) {
        if (item.controls.item_part_type.value != ItemType.PART) {
          const _item = item as unknown as FormGroupIdDecorator<IQuoteItem>;
          context.push(_item.id);
          return new IQuoteItemWithContextDecorator(
            { ..._item.value, id: _item.id },
            context,
            0,
          );
        } else {
          const _item = item as unknown as FormGroupIdDecorator<IQuoteItem>;
          return new IQuoteItemWithContextDecorator(
            { ..._item.value, id: _item.id },
            context,
            ++insertAt,
          );
        }
      }

      if (!match && item.controls.parts?.length) {
        const _item =
          item as unknown as FormGroupIdDecorator<IQuoteItemWithContextDecorator>;
        context.push(_item.id);
        match = this.getQuoteItemAtLine(
          linenumber,
          item.controls.parts,
          match,
          context,
        );
        if (!match) {
          context.pop();
        } else {
          if (match instanceof IQuoteItemWithContextDecorator) {
            return match;
          }
        }
      }

      if (match instanceof IQuoteItemWithContextDecorator) {
        return match;
      }
      ++insertAt;
    }

    if (!match) {
      return match;
    }

    if (match instanceof IQuoteItemWithContextDecorator) {
      return match;
    }

    return new IQuoteItemWithContextDecorator(match, context, ++insertAt);
  }

  public getQuoteItemByInternalId(
    id: number,
    container:
      | FormArray<FormGroupIdDecorator<IQuoteItemFormGroup>>
      | undefined = undefined,
  ): FormGroup<IQuoteItemFormGroup> | undefined {
    if (!container) {
      container = this._formgroup?.controls['quote_items'] as FormArray<
        FormGroupIdDecorator<IQuoteItemFormGroup>
      >;
      if (!container) {
        return undefined;
      }
    }

    for (const item of container.controls) {
      if (item.id == id) {
        return item as unknown as FormGroup<IQuoteItemFormGroup>;
      }

      if (item.parts) {
        const match = this.getQuoteItemByInternalId(id, item.parts);

        if (match) {
          return match;
        }
      }
    }

    return undefined;
  }

  make_quotemeta_formgroup(meta?: IQuoteMeta): FormGroup<IQuoteMetaFormGroup> {
    if (!this.gateway) {
      throw Error('No gateway defined');
    }

    return new FormGroup<IQuoteMetaFormGroup>({
      quote_ref: new FormControl<string>(meta?.quote_ref || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern('^[0-9]{5}.*')],
        asyncValidators: [
          uniqueQuoteReferenceValidator(this.gateway, this.quote_id),
        ],
        updateOn: 'change',
      }),
      description: new FormControl<string>(meta?.description || '', {
        nonNullable: true,
      }),
      paragraph: new FormControl<string>(meta?.paragraph || '', {
        nonNullable: true,
      }),
      customer_id: new FormControl<string>(meta?.customer_id || '', {
        nonNullable: true,
      }),
      consultant: new FormControl<string>(meta?.consultant || '', {
        nonNullable: true,
      }),
      attention: new FormControl<string>(meta?.attention || '', {
        nonNullable: true,
      }),
      salutation: new FormControl<string>(meta?.salutation || '', {
        nonNullable: true,
      }),
      dtm_quote: new FormControl<Date | string | null>(
        new Date(meta?.dtm_quote || 'now'),
      ),
    });
  }
}

/**
 * !FIXME: All quote_items throughout the system need to be of
 *         the FormGroupIdDecorator type.
 *         Need to correct the classes so that they
 *         are not simply wrappers around FormGroup
 */
export class Quote extends BaseQuote<IQuote> implements IBaseQuote {
  quote_item_hierarchy: IQuoteItem[];
  protected override _formgroup: FormGroup<IQuoteFormGroup>;

  get price_adjustment(): FormArray<FormGroup<IQuotePriceAdjustmentFormGroup>> {
    return this._formgroup.controls.price_adjustment as FormArray<
      FormGroup<IQuotePriceAdjustmentFormGroup>
    >;
  }

  constructor(quote?: IQuote, gateway?: QuoteDataGatewayService) {
    super(quote, gateway);
    this._formgroup = this.make_formgroup(quote);

    this.addDefaultQuoteAdjustments();
    if (quote?.price_adjustment && quote.price_adjustment.length) {
      for (const adjustment of quote.price_adjustment) {
        for (const fg_adjustment of this.price_adjustment.controls) {
          if (
            fg_adjustment.controls.adjustment_code.value ==
            adjustment.adjustment_code
          ) {
            fg_adjustment.controls.adjustment_type.setValue(
              adjustment.adjustment_type,
            );
            fg_adjustment.controls.adjustment_value.setValue(
              adjustment.adjustment_value,
            );
          }
        }
      }
    }

    this.quote_item_hierarchy = quote?.quote_item_hierarchy || [];
  }

  make_formgroup(quote?: IQuote): FormGroup<IQuoteFormGroup> {
    if (!this.gateway) {
      throw Error('No gateway defined');
    }
    const fg = new FormGroup<IQuoteFormGroup>({
      quote_id: new FormControl<string | null>(quote?.quote_id || ''),
      quote_type: new FormControl<QuoteType>(QuoteType.STANDARD, {
        nonNullable: true,
      }),
      quote_meta: this.make_quotemeta_formgroup(quote?.quote_meta),
      quote_items: new FormArray<FormGroup<IQuoteItemFormGroup>>([]),
      price_adjustment: new FormArray<
        FormGroup<IQuotePriceAdjustmentFormGroup>
      >([]),
    });

    return fg;
  }

  get formgroup(): FormGroup<IQuoteFormGroup> {
    return this._formgroup;
  }

  get total(): Decimal {
    return this.getTotal(this.partTotal);
  }

  get total_inc_markup(): Decimal {
    return this.getTotal(this.partTotalIncMarkup.bind(this));
  }

  getTotal(
    partTotalCalulation: (item: FormGroup<IQuoteItemFormGroup>) => Decimal,
    items: FormArray<FormGroup<IQuoteItemFormGroup>> | undefined = undefined,
    total: Decimal = new Decimal(0),
  ): Decimal {
    if (!items) {
      items = this._formgroup.controls.quote_items as FormArray<
        FormGroup<IQuoteItemFormGroup>
      >;
    }

    for (const item of items.controls) {
      // !FIXME: convert this to a behavior, include_in_total: boolean
      if (
        item.controls.item_part_type.value == ItemType.PART ||
        item.controls.item_part_type.value == ItemType.ENCLOSURE
      ) {
        total = total.add(partTotalCalulation(item));
      }

      if (item.controls.parts) {
        total = this.getTotal(partTotalCalulation, item.controls.parts, total);
      }
    }

    return total;
  }

  gross_up(total: Decimal) {
    let gross_up_fixed = new Decimal(0);

    for (const adj of this._formgroup.controls.price_adjustment.controls) {
      if (adj.controls.adjustment_code.value == 'GROSS') {
        const val = adj.controls.adjustment_value.value || 0;

        if (
          adj.controls.adjustment_type.value == PriceAdjustmentType.PERCENTAGE
        ) {
          // gross up can be rounded up here, round(1/(1-%age))

          gross_up_fixed = total.div(1 - val / 100);
        } else {
          gross_up_fixed = new Decimal(val);
          gross_up_fixed = gross_up_fixed.add(total);
        }
      }
    }

    return gross_up_fixed;
  }

  discount(total: Decimal) {
    let discount_fixed = new Decimal(0);

    for (const adj of this._formgroup.controls.price_adjustment.controls) {
      if (adj.controls.adjustment_code.value == 'DISC') {
        const val = adj.controls.adjustment_value.value || 0;

        if (
          adj.controls.adjustment_type.value == PriceAdjustmentType.PERCENTAGE
        ) {
          discount_fixed = total.mul(val / 100);
        } else {
          discount_fixed = new Decimal(val);
        }
      }
    }

    return discount_fixed;
  }

  get headline_costs(): any {
    const acc = this.calculate_headline_costs(
      this._formgroup.controls.quote_items,
    );

    const costs: any = [];
    for (const item of acc) {
      const headline_cost = costs.find(
        (_item: any) => _item.headline_cost == item.headline_cost,
      );
      if (headline_cost) {
        headline_cost.total = headline_cost.total.add(item.total);
        headline_cost.total_inc_markup = headline_cost.total_inc_markup.add(
          item.total_inc_markup,
        );
      } else {
        costs.push(item);
      }
    }

    costs.sort((_item1: any, _item2: any) => {
      if (_item1.headline_cost < _item2.headline_cost) {
        return -1;
      }

      if (_item1.headline_cost > _item2.headline_cost) {
        return 1;
      }

      return 0;
    });

    const init_val = new Decimal(0);
    const total = costs.reduce(
      (acc: Decimal, curVal: any) => acc.add(curVal.total_inc_markup),
      init_val,
    );

    /**@TODO: apply gross up, discounts here*/
    const total_inc_grossup = this.gross_up(total);
    const gross_up_amount = total_inc_grossup.sub(total);
    const discount_amount = this.discount(total_inc_grossup);

    for (const headline_cost of costs) {
      const proportion = headline_cost.total_inc_markup.div(total);
      headline_cost.total_inc_grossup = headline_cost.total_inc_markup.add(
        gross_up_amount.mul(proportion),
      );
      headline_cost.total_inc_discount = headline_cost.total_inc_grossup.sub(
        discount_amount.mul(proportion),
      );
    }

    return costs;
  }

  private calculate_headline_costs(container: any, acc: any = []): any {
    for (const item of container.controls) {
      if (item.group_ref) {
        acc.push({
          headline_cost: item.group_ref,
          total: this.partTotal(item),
          total_inc_markup: this.partTotalIncMarkup(item),
        });
      }

      if (item.parts) {
        acc = this.calculate_headline_costs(item.parts, acc);
      }
    }

    return acc;
  }

  partTotal(item: FormGroup<IQuoteItemFormGroup>): Decimal {
    const price = new Decimal(item.controls.unit_price?.value || 0);
    const qty = new Decimal(item.controls.quantity?.value || 0);

    return price.mul(qty);
  }

  partTotalIncMarkup(item: FormGroup<IQuoteItemFormGroup>): Decimal {
    const total = this.partTotal(item);
    const markup = new Decimal(item.controls.markup?.value || 0);

    return total.mul(markup);
  }

  private addDefaultQuoteAdjustments() {
    const pj1 = (code: string) => {
      return new FormGroup<IQuotePriceAdjustmentFormGroup>({
        adjustment_code: new FormControl<string>(code, { nonNullable: true }),
        adjustment_type: new FormControl<PriceAdjustmentType>(
          PriceAdjustmentType.PERCENTAGE,
          { nonNullable: true },
        ),
        adjustment_value: new FormControl<number | null>(null),
      });
    };

    this._formgroup.controls.price_adjustment.push(pj1('GROSS'));
    this._formgroup.controls.price_adjustment.push(pj1('DISC'));
  }
}

export class MaintenanceQuote extends BaseQuote<IMaintenanceQuote> {
  formgroup: FormGroup<IMaintenanceQuoteFormGroup>;

  get total(): Decimal {
    return this.getTotal(this.partTotal);
  }

  get total_inc_markup(): Decimal {
    return this.getTotal(this.partTotalIncMarkup.bind(this));
  }

  constructor(quote?: IMaintenanceQuote, gateway?: QuoteDataGatewayService) {
    super(quote, gateway);
    this.formgroup = this.make_formgroup(quote);
  }

  make_formgroup(
    quote?: IMaintenanceQuote,
  ): FormGroup<IMaintenanceQuoteFormGroup> {
    const contract_lines =
      (quote?.contract_lines as IMaintenanceContractLineItem[]) || [];

    const fg = new FormGroup<IMaintenanceQuoteFormGroup>({
      quote_id: new FormControl<string>(quote?.quote_id || '', {
        nonNullable: true,
      }),
      quote_type: new FormControl<QuoteType>(QuoteType.MAINTENANCE, {
        nonNullable: true,
      }),
      quote_meta: this.make_quotemeta_formgroup(quote?.quote_meta),
      contract_lines: new FormArray<
        FormControl<IMaintenanceContractLineItem | null | undefined>
      >([]),
      quote_items: new FormArray<FormGroup<IQuoteItemFormGroup>>([]),
      quote_note: new FormControl<Delta | null>(quote?.quote_note || null),
    });

    for (const contract_line of contract_lines) {
      if (contract_line.line_item_type.toLowerCase() === 'nett') {
        fg.controls.contract_lines.push(this._makeNettLine(contract_line));
      } else {
        fg.controls.contract_lines.push(this._makeContractLine(contract_line));
      }
    }

    return fg;
  }

  _makeContractLine(
    contractLine?: IMaintenanceContractLineItem,
  ): FormControl<IMaintenanceContractLineItem | undefined | null> {
    contractLine = {
      ...contractLine,
      line_item_type: 'contract',
    } as IMaintenanceContractLineItem;

    return new FormControl(contractLine);
  }

  _makeNettLine(
    lineItem?: IMaintenanceContractLineItem,
  ): FormControl<IMaintenanceContractLineItem | undefined | null> {
    lineItem = {
      ...lineItem,
      line_item_type: 'nett',
    } as IMaintenanceContractLineItem;
    return new FormControl(lineItem);
  }

  getTotal(
    partTotalCalulation: (item: FormGroup<IQuoteItemFormGroup>) => Decimal,
    items: FormArray<FormGroup<IQuoteItemFormGroup>> | undefined = undefined,
    total: Decimal = new Decimal(0),
  ): Decimal {
    if (!items) {
      items = this.formgroup.controls.quote_items as FormArray<
        FormGroup<IQuoteItemFormGroup>
      >;
    }

    for (const item of items.controls) {
      // !FIXME: convert this to a behavior, include_in_total: boolean
      if (
        item.controls.item_part_type.value == ItemType.PART ||
        item.controls.item_part_type.value == ItemType.ENCLOSURE
      ) {
        total = total.add(partTotalCalulation(item));
      }

      if (item.controls.parts) {
        total = this.getTotal(partTotalCalulation, item.controls.parts, total);
      }
    }

    return total;
  }

  partTotal(item: FormGroup<IQuoteItemFormGroup>): Decimal {
    const price = new Decimal(item.controls.unit_price?.value || 0);
    const qty = new Decimal(item.controls.quantity?.value || 0);

    return price.mul(qty);
  }

  partTotalIncMarkup(item: FormGroup<IQuoteItemFormGroup>): Decimal {
    const total = this.partTotal(item);
    const markup = new Decimal(item.controls.markup?.value || 0);

    return total.mul(markup);
  }
}

export abstract class QuoteItem implements IQuoteItem {
  private static internal_id = 1;
  private __id: number;
  item_line_number = 0;
  item_part_number = 0;
  quote_item_line_number = 0;
  quote_item_part_number = 0;
  kind = 'standard';
  item_part_type = '';
  protected _formGroup!: FormGroupIdDecorator<IQuoteItemFormGroup>;
  protected _parts: Partial<IQuoteItem>[] = [];

  constructor() {
    this.__id = QuoteItem.internal_id++;
  }

  get internalId(): number {
    return this.__id;
  }

  abstract get itemType(): ItemType;
  abstract get formGroup(): QuoteItemFormGroupWithId | null;
  abstract getDisplayTotal(): Decimal;
  abstract getCalculationTotal(): Decimal;
}

export abstract class AggregateQuoteItem extends QuoteItem {
  constructor(part: Partial<IQuoteItem>) {
    super();
    if (part.parts?.length) {
      part.parts.map((p) => {
        if (!p.item_part_type) {
          p.item_part_type = ItemType.PART;
        }
      });

      this._parts = part.parts;
    }
  }

  get parts(): Partial<IQuoteItem>[] {
    return this._parts;
  }

  get partsControls(): FormGroup<IQuoteItemFormGroup>[] | undefined {
    const fg = this._formGroup as unknown as FormGroup<IQuoteItemFormGroup>;
    return fg.controls.parts?.controls;
  }
}

export abstract class PhysicalAggregateQuoteItem extends AggregateQuoteItem {
  getDisplayTotal(): Decimal {
    const item = this._formGroup.value;
    console.log('PhysicalAggregateQuoteItem: ', item);
    return new Decimal(0);
  }

  getCalculationTotal(): Decimal {
    return new Decimal(0);
  }
}

export abstract class SumOfPartsAggregateQuoteItem extends AggregateQuoteItem {
  getDisplayTotal(): Decimal {
    const item = this._formGroup.value;
    console.log('SumOfAggregates item: ', item);
    return new Decimal(0);
  }

  getCalculationTotal(): Decimal {
    return new Decimal(0);
  }
}

export class PartQuoteItem extends QuoteItem {
  protected type: ItemType = ItemType.PART;

  get itemType(): ItemType {
    return this.type;
  }

  constructor(part: Partial<IQuoteItem>) {
    super();
    const fg = new FormGroup<IQuoteItemFormGroup>({
      quote_item_id: new FormControl<string>(part.quote_item_id || '', {
        nonNullable: true,
      }),
      kind: new FormControl<string>('standard', { nonNullable: true }),
      item_part_id: new FormControl<string>(
        part.item_part_id || part.quote_item_part_id || '',
        { nonNullable: true },
      ),
      item_part_type: new FormControl<ItemType>(ItemType.PART, {
        nonNullable: true,
      }),
      quote_item_description: new FormControl<string>(
        part.description || part.quote_item_description || '',
        { nonNullable: true },
      ),
      quantity: new FormControl<number>(part.quantity || 0, {
        nonNullable: true,
      }),
      unit_price: new FormControl<number>(part.price || part.unit_price || 0, {
        nonNullable: true,
      }),
      markup: new FormControl<number>(part.selectedMarkup || part.markup || 0, {
        nonNullable: true,
      }),
      item_line_number: new FormControl<number>(part.item_line_number || 1, {
        nonNullable: true,
      }),
      item_part_number: new FormControl<number>(part.item_part_number || 0, {
        nonNullable: true,
      }),
      configurations: new FormArray<FormGroup<any>>([]),
      flg_price_override: new FormControl<boolean>(
        part.flg_price_override || false,
        { nonNullable: true },
      ),
    });

    if (part.configurations) {
      for (const configuration of part.configurations) {
        const cfg = this.makeConfigurationFormGroup(configuration);
        const configuration_items = cfg.controls['configuration'] as FormArray<
          FormGroup<any>
        >;
        for (const item of configuration.configuration) {
          if (item.is_applicable) {
            configuration_items.push(this.makeConfigurationItemFormGroup(item));
          }
        }

        fg.controls.configurations.push(cfg);
      }
    }

    this._formGroup = new FormGroupIdDecorator<IQuoteItemFormGroup>(
      this.internalId,
      fg,
      part.part_group_ref,
    );
  }

  makeConfigurationFormGroup(configuration: any): FormGroup<any> {
    return new FormGroup<any>({
      category_name: new FormControl<string>(configuration.category_name),
      category_description: new FormControl<string>(
        configuration.category_description,
      ),
      configuration: new FormArray<FormGroup<any>>([]),
    });
  }

  makeConfigurationItemFormGroup(configuration: any) {
    return new FormGroup<any>({
      quote_item_configuration_sequence_id: new FormControl<string>(
        configuration.quote_item_configuration_sequence_id || '',
        { nonNullable: true },
      ),
      configuration_item_id: new FormControl<string>(
        configuration.configuration_item_id,
        { nonNullable: true },
      ),
      configuration_item_name: new FormControl<string>(
        configuration.configuration_item_name,
      ),
      configuration_item_description: new FormControl<string>(
        configuration.configuration_item_description,
      ),
      quantity: new FormControl<number>(
        configuration.quantity || configuration.default_quantity || 0,
        { nonNullable: true },
      ),
    });
  }

  get formGroup(): FormGroupIdDecorator<IQuoteItemFormGroup> {
    return this._formGroup;
  }

  getDisplayTotal(): Decimal {
    const item: IQuoteItem = this._formGroup.value;
    const unitPrice = new Decimal(item.unit_price || 0);
    const qty = new Decimal(item.quantity || 0);

    return unitPrice.mul(qty);
  }

  getCalculationTotal(): Decimal {
    const item: IQuoteItem = this._formGroup.value;
    const unitPrice = new Decimal(item.unit_price || 0);
    const qty = new Decimal(item.quantity || 0);

    return unitPrice.mul(qty);
  }
}

export class EnclosureQuoteItem
  extends PhysicalAggregateQuoteItem
  implements IPhysicalAggregateQuoteItem
{
  protected type;

  get itemType(): ItemType {
    return this.type;
  }

  _formgroup: FormGroupIdDecorator<IQuoteItemFormGroup>;

  constructor(part: Partial<IQuoteItem>, type: ItemType = ItemType.ENCLOSURE) {
    super(part);
    this.type = type;
    const fg = new FormGroup<IQuoteItemFormGroup>({
      quote_item_id: new FormControl<string>(part.quote_item_id || '', {
        nonNullable: true,
      }),
      kind: new FormControl<string>('standard', { nonNullable: true }),
      item_part_id: new FormControl<string>(part.item_part_id || '', {
        nonNullable: true,
      }),
      item_part_type: new FormControl<ItemType>(
        this.type || ItemType.ENCLOSURE,
        { nonNullable: true },
      ),
      quote_item_description: new FormControl<string>(
        part.description || part.quote_item_description || '',
        { nonNullable: true },
      ),
      quantity: new FormControl<number>(part.quantity || 1, {
        nonNullable: true,
      }),
      unit_price: new FormControl<number>(part.price || part.unit_price || 0, {
        nonNullable: true,
      }),
      flg_price_override: new FormControl<boolean>(
        part.flg_price_override || false,
        { nonNullable: true },
      ),
      markup: new FormControl<number>(part.markup || 0, { nonNullable: true }),
      parts: new FormArray<FormGroup<IQuoteItemFormGroup>>([]),
      item_line_number: new FormControl<number>(part.item_line_number || 1, {
        nonNullable: true,
      }),
      item_part_number: new FormControl<number>(part.item_part_number || 0, {
        nonNullable: true,
      }),
      configurations: new FormArray<FormGroup<any>>([]),
    });

    fg.controls.parts?.setParent(fg);
    this._formgroup = new FormGroupIdDecorator(
      this.internalId,
      fg,
      part.part_group_ref,
    );
  }

  get formGroup(): QuoteItemFormGroupWithId {
    return this._formgroup;
  }
}

export class CompositeQuoteItem
  extends SumOfPartsAggregateQuoteItem
  implements ISumOfPartsAggregateQuoteItem
{
  protected type;

  get itemType(): ItemType {
    return this.type;
  }

  _formgroup: FormGroupIdDecorator<IQuoteItemFormGroup>;

  constructor(part: Partial<IQuoteItem>, type: ItemType = ItemType.COMPOSITE) {
    super(part);
    this.type = type;
    const fg = new FormGroup<IQuoteItemFormGroup>({
      quote_item_id: new FormControl<string>(part.quote_item_id || '', {
        nonNullable: true,
      }),
      kind: new FormControl<string>('standard', { nonNullable: true }),
      item_composite_id: new FormControl<string>(
        part.item_composite_id || part.quote_item_part_id || '',
        { nonNullable: true },
      ),
      item_part_type: new FormControl<ItemType>(
        this.type || ItemType.COMPOSITE,
        { nonNullable: true },
      ),
      quote_item_description: new FormControl<string>(
        part.description || part.quote_item_description || '',
        { nonNullable: true },
      ),
      quantity: new FormControl<number>(part.quantity || 1, {
        nonNullable: true,
      }),
      unit_price: new FormControl<number>(part.price || part.unit_price || 0, {
        nonNullable: true,
      }),
      markup: new FormControl<number>(part.markup || 0, { nonNullable: true }),
      parts: new FormArray<FormGroup<IQuoteItemFormGroup>>([]),
      item_line_number: new FormControl<number>(part.item_line_number || 1, {
        nonNullable: true,
      }),
      item_part_number: new FormControl<number>(part.item_part_number || 0, {
        nonNullable: true,
      }),
      configurations: new FormArray<FormGroup<any>>([]),
    });

    fg.controls.parts?.setParent(fg);
    this._formgroup = new FormGroupIdDecorator(this.internalId, fg);
  }

  get formGroup(): QuoteItemFormGroupWithId {
    return this._formgroup;
  }
}

export class FinishedGoodQuoteItem
  extends SumOfPartsAggregateQuoteItem
  implements ISumOfPartsAggregateQuoteItem
{
  protected type = ItemType.FINISHEDGOOD;
  get itemType(): ItemType {
    return this.type;
  }

  private _formgroup: FormGroupIdDecorator<IQuoteItemFormGroup>;

  constructor(part: Partial<IQuoteItem>) {
    super(part);
    const fg = new FormGroup<IQuoteItemFormGroup>({
      quote_item_id: new FormControl<string>(part.quote_item_id || '', {
        nonNullable: true,
      }),
      kind: new FormControl<string>('standard', { nonNullable: true }),
      item_part_id: new FormControl<string>(this.internalId.toString() || '', {
        nonNullable: true,
      }),
      item_part_type: new FormControl<ItemType>(ItemType.FINISHEDGOOD, {
        nonNullable: true,
      }),
      quote_item_description: new FormControl<string>(
        part.description || part.quote_item_description || '',
        { nonNullable: true },
      ),
      quantity: new FormControl<number>(part.quantity || 1, {
        nonNullable: true,
      }),
      parts: new FormArray<FormGroup<IQuoteItemFormGroup>>([]),
      item_line_number: new FormControl<number>(part.item_line_number || 1, {
        nonNullable: true,
      }),
      item_part_number: new FormControl<number>(part.item_part_number || 0, {
        nonNullable: true,
      }),
      configurations: new FormArray<FormGroup<any>>([]),
    });

    fg.controls.parts?.setParent(fg);
    this._formgroup = new FormGroupIdDecorator(this.internalId, fg);
  }

  get formGroup(): QuoteItemFormGroupWithId {
    return this._formgroup;
  }
}

export class TextQuoteItem extends QuoteItem {
  private _formgroup = null;

  get itemType() {
    return ItemType.PART;
  }

  get formGroup() {
    return this._formgroup;
  }

  getDisplayTotal(): Decimal {
    return new Decimal(0);
  }

  getCalculationTotal(): Decimal {
    return new Decimal(0);
  }
}

export class NettPriceItem extends QuoteItem {
  get itemType() {
    return ItemType.NETTPRICE;
  }

  private _formgroup: QuoteItemFormGroupWithId;

  constructor(part: Partial<IQuoteItem>) {
    super();

    const fg = new FormGroup({
      quote_item_id: new FormControl<string>(part.quote_item_id || '', {
        nonNullable: true,
      }),
      item_part_type: new FormControl<ItemType>(ItemType.NETTPRICE, {
        nonNullable: true,
      }),
      quote_item_description: new FormControl<string>(
        part.description || part.quote_item_description || 'nett price',
      ),
      quantity: new FormControl<number>(0),
      item_line_number: new FormControl<number>(part.item_line_number || 1, {
        nonNullable: true,
      }),
      item_part_number: new FormControl<number>(0, { nonNullable: true }),
    });

    this._formgroup = new FormGroupIdDecorator(this.internalId, fg);
  }

  get formGroup(): QuoteItemFormGroupWithId {
    return this._formgroup;
  }

  getDisplayTotal(): Decimal {
    return new Decimal(0);
  }

  getCalculationTotal(): Decimal {
    return new Decimal(0);
  }
}

export class SeparatorQuoteItem extends QuoteItem {
  private _style: SepratorStyle = SepratorStyle.SOLID;
  private _formgroup = null;

  get itemType() {
    return ItemType.PART;
  }

  get style(): SepratorStyle {
    return this._style;
  }

  set style(newStyle: SepratorStyle) {
    this._style = newStyle;
  }

  get formGroup() {
    return this._formgroup;
  }

  getDisplayTotal(): Decimal {
    return new Decimal(0);
  }

  getCalculationTotal(): Decimal {
    return new Decimal(0);
  }
}
